import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Crown, ShieldCheck, Ticket, ArrowRight, Activity, UserPlus, Building2, MessageSquare, Gift } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader } from "@/components/admin/AdminShared";

const opCards = [
  ["users", "Total Members", Users, "/admin/users"],
  ["vip_active", "Active VIP", Crown, "/admin/vip"],
  ["verifications_pending", "Pending Verifications", ShieldCheck, "/admin/verifications"],
  ["tickets_open", "Open Support Tickets", Ticket, "/admin/tickets"],
];

const activityIcon = (kind) => {
  if (kind?.includes("registration")) return UserPlus;
  if (kind?.includes("verification")) return ShieldCheck;
  if (kind?.includes("vip")) return Crown;
  if (kind?.includes("ticket")) return MessageSquare;
  if (kind?.includes("referral")) return Gift;
  return Activity;
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export default function AdminDashboard() {
  useSEO("Admin");
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);

  const pending = [
    { count: stats.verifications_pending, label: "broker verification(s) awaiting review", to: "/admin/verifications", icon: ShieldCheck },
    { count: stats.vip_pending, label: "direct VIP request(s) to handle", to: "/admin/vip", icon: Crown },
    { count: stats.tickets_open, label: "open support ticket(s)", to: "/admin/tickets", icon: Ticket },
  ].filter((p) => (p.count ?? 0) > 0);

  return (
    <div>
      <AdminHeader title="Dashboard" subtitle="Operational overview" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {opCards.map(([key, label, Icon, to]) => (
          <Link key={key} to={to} className="card-surface hover-glow rounded-xl p-5" data-testid={`stat-${key}`}>
            <div className="flex items-center justify-between">
              <div className="h-9 w-9 rounded-lg bg-brand-cyan/10 flex items-center justify-center"><Icon className="h-4.5 w-4.5 text-brand-cyan" /></div>
              <ArrowRight className="h-4 w-4 text-slate-600 rtl-mirror" />
            </div>
            <p className="font-display text-3xl font-bold text-white mt-3">{stats[key] ?? 0}</p>
            <p className="text-xs text-slate-400 mt-0.5">{label}</p>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <div className="card-surface rounded-xl p-5" data-testid="pending-actions">
          <h2 className="font-display font-semibold text-white mb-3">Pending Actions</h2>
          {pending.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">All caught up — nothing needs attention.</p>
          ) : (
            <div className="space-y-2">
              {pending.map((p) => (
                <Link key={p.to} to={p.to} className="flex items-center gap-3 rounded-lg bg-white/5 hover:bg-white/[0.08] px-4 py-3 transition-colors" data-testid={`pending-${p.to.split("/").pop()}`}>
                  <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center"><p.icon className="h-4 w-4 text-amber-400" /></div>
                  <span className="text-sm text-slate-200"><span className="font-semibold text-white">{p.count}</span> {p.label}</span>
                  <ArrowRight className="h-4 w-4 text-slate-600 ml-auto rtl-mirror" />
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card-surface rounded-xl p-5" data-testid="recent-activity">
          <h2 className="font-display font-semibold text-white mb-3">Recent Activity</h2>
          {activity.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No recent activity yet.</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {activity.map((a, i) => {
                const Icon = activityIcon(a.kind);
                return (
                  <div key={i} className="flex items-start gap-3 px-2 py-2 rounded-lg hover:bg-white/[0.03]">
                    <div className="h-7 w-7 rounded-md bg-brand-cyan/10 flex items-center justify-center shrink-0 mt-0.5"><Icon className="h-3.5 w-3.5 text-brand-cyan" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-300 leading-snug">{a.message}</p>
                      <p className="text-[11px] text-slate-600">{timeAgo(a.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
